import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Plus, Trash2, Building2, Shield, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { listAidProviders } from '@/api/aid';
import type { WildfireValues, MutualAidRow } from './WildfireSchema';
import { cn } from '@/lib/utils';

export interface AidProviderStation {
  id: number;
  name: string;
}

export interface AidProviderRecord {
  key: string;
  kind: 'fpa' | 'org';
  id: number;
  name: string;
  org_type?: string | null;
  stations?: AidProviderStation[];
  is_shadow?: boolean | null;
}

interface MutualAidPickerProps {
  /**
   * The FPA the report belongs to. Passed to the API so that FPA is left out
   * of its own options — an FPA does not assist itself.
   *
   * Must be the FIRE's tenant, not the signed-in user's. A super admin has no
   * meaningful own FPA (resolve_tenant_filter passes their request through
   * verbatim), so keying off the caller would offer Roodeplaat as its own
   * assistant while editing a Roodeplaat fire. Same trap the tenantId prop on
   * FirefighterPicker exists to close.
   */
  tenantId?: number | null;
  /**
   * Providers already named on the fire being edited. The stored rows carry
   * the provider name, so seeding from them means an option that has scrolled
   * out of the fetched list still renders by name instead of a bare id.
   */
  seedProviders?: AidProviderRecord[];
}

const ORG_TYPE_LABELS: Record<string, string> = {
  fire_department: 'Fire department',
  private_service: 'Private service',
  government: 'Government',
  ngo: 'NGO',
  other: 'Other',
};

const BLANK: MutualAidRow = {
  providerKey: '',
  providerKind: 'fpa',
  providerId: 0,
  providerName: '',
  stationIds: [],
  people: 0,
  vehicles: 0,
  vehicleDetails: '',
  hours: 0,
  note: '',
};

/**
 * Repeating rows recording assistance RECEIVED on a fire: who turned out, from
 * which station, and what they brought.
 *
 * Follows the controlled-array onChange pattern used by ContactList rather
 * than useFieldArray, which this codebase does not use anywhere.
 */
export function MutualAidPicker({ tenantId, seedProviders }: MutualAidPickerProps = {}) {
  const form = useFormContext<WildfireValues>();
  const rows = (form.watch('mutualAid') ?? []) as MutualAidRow[];

  const [providers, setProviders] = useState<AidProviderRecord[]>(seedProviders ?? []);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = (await listAidProviders(tenantId ?? undefined)) as unknown as {
          providers: AidProviderRecord[];
        };
        if (cancelled) return;
        const fetched = data.providers || [];
        // Merge rather than replace: a provider named on this fire but since
        // retired is no longer in the directory, and dropping it would blank
        // the row the user is looking at.
        const byKey = new Map<string, AidProviderRecord>();
        for (const p of seedProviders ?? []) byKey.set(p.key, p);
        for (const p of fetched) byKey.set(p.key, p);
        setProviders(Array.from(byKey.values()));
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading aid providers:', err);
          setLoadError('Could not load the list of organisations.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // seedProviders is read once on mount; re-running on identity change would
    // refetch on every render of the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const byKey = useMemo(() => {
    const map = new Map<string, AidProviderRecord>();
    for (const p of providers) map.set(p.key, p);
    return map;
  }, [providers]);

  const grouped = useMemo(() => {
    const fpas = providers.filter((p) => p.kind === 'fpa');
    const orgs = providers.filter((p) => p.kind === 'org');
    return { fpas, orgs };
  }, [providers]);

  const setRows = (next: MutualAidRow[]) =>
    form.setValue('mutualAid', next, { shouldDirty: true, shouldValidate: true });

  const update = (index: number, patch: Partial<MutualAidRow>) =>
    setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const toggleStation = (index: number, stationId: number) => {
    const current = rows[index]?.stationIds ?? [];
    update(index, {
      stationIds: current.includes(stationId)
        ? current.filter((id) => id !== stationId)
        : [...current, stationId],
    });
  };

  const addRow = () => setRows([...rows, { ...BLANK }]);
  const removeRow = (index: number) => setRows(rows.filter((_, i) => i !== index));

  const chooseProvider = (index: number, key: string) => {
    const provider = byKey.get(key);
    if (!provider) return;
    update(index, {
      providerKey: provider.key,
      providerKind: provider.kind,
      providerId: provider.id,
      providerName: provider.name,
      // Stations belong to the previous organisation; keeping them would
      // attach depots to a brigade that does not own them.
      stationIds: [],
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Who came to help, and what they brought. Leave this empty if your own
        crew handled it. These numbers are tracked separately from the people on
        scene above, so they do not change your own totals.
      </p>

      {loadError && <p className="text-sm text-destructive">{loadError}</p>}

      {rows.length === 0 && (
        <p className="text-sm text-muted-foreground">No assistance recorded.</p>
      )}

      {rows.map((row, index) => {
        const provider = byKey.get(row.providerKey);
        const stations = provider?.stations ?? [];
        const selectedStations = stations.filter((s) =>
          (row.stationIds ?? []).includes(s.id),
        );
        return (
          <div key={index} className="rounded-lg border p-3 space-y-3">
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-1">
                <Label>Who assisted</Label>
                <Select
                  value={row.providerKey || undefined}
                  onValueChange={(value) => chooseProvider(index, value)}
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loading ? 'Loading…' : 'Choose an FPA or organisation'} />
                  </SelectTrigger>
                  <SelectContent>
                    {grouped.fpas.length > 0 && (
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                        Fire Protection Associations
                      </div>
                    )}
                    {grouped.fpas.map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        <span className="flex items-center gap-2">
                          <Shield className="h-3.5 w-3.5" />
                          {p.name}
                          {p.is_shadow && (
                            <Badge variant="outline" className="text-[10px]">
                              not on system
                            </Badge>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                    {grouped.orgs.length > 0 && (
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                        Other organisations
                      </div>
                    )}
                    {grouped.orgs.map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        <span className="flex items-center gap-2">
                          <Building2 className="h-3.5 w-3.5" />
                          {p.name}
                          {p.org_type && (
                            <span className="text-xs text-muted-foreground">
                              {ORG_TYPE_LABELS[p.org_type] ?? p.org_type}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="mt-6"
                onClick={() => removeRow(index)}
                aria-label="Remove this organisation"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {stations.length > 0 && (
              <div className="space-y-1">
                <Label>Stations</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-between font-normal"
                    >
                      <span className={cn(!selectedStations.length && 'text-muted-foreground')}>
                        {selectedStations.length
                          ? selectedStations.map((s) => s.name).join(', ')
                          : 'Which stations (optional)'}
                      </span>
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
                    {stations.map((s) => (
                      <label
                        key={s.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
                      >
                        <Checkbox
                          checked={(row.stationIds ?? []).includes(s.id)}
                          onCheckedChange={() => toggleStation(index, s.id)}
                        />
                        <span className="text-sm">{s.name}</span>
                      </label>
                    ))}
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  Pick every depot that turned out. The people and vehicle counts
                  below cover the whole turnout, not one station each.
                </p>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label>People</Label>
                <Input
                  type="number"
                  min="0"
                  value={row.people ?? 0}
                  onChange={(e) => update(index, { people: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <Label>Vehicles</Label>
                <Input
                  type="number"
                  min="0"
                  value={row.vehicles ?? 0}
                  onChange={(e) => update(index, { vehicles: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <Label>Hours on scene</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={row.hours ?? 0}
                  onChange={(e) => update(index, { hours: Number(e.target.value) })}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Vehicles / resources</Label>
              <Input
                placeholder="e.g. Pumper 3, skid unit 1"
                value={row.vehicleDetails ?? ''}
                onChange={(e) => update(index, { vehicleDetails: e.target.value })}
              />
            </div>

            <div className="space-y-1">
              <Label>Note</Label>
              <Input
                placeholder="Anything worth recording about their attendance"
                value={row.note ?? ''}
                onChange={(e) => update(index, { note: e.target.value })}
              />
            </div>
          </div>
        );
      })}

      <Button type="button" variant="outline" onClick={addRow} disabled={loading}>
        <Plus className="mr-2 h-4 w-4" />
        Add an organisation
      </Button>
    </div>
  );
}

export default MutualAidPicker;
